import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { LaunchError } from "../utils/errors";
import { renewToken } from "./auth";

import { store } from "../app/store";

const REPORT_PDF_FUNCTION_NAMES = [
  "startReportPdfExport",
  "getReportPdfSnapshot",
  "generateReportPdf",
];

const buildReportPdfUrl = (endpoint, token) => {
  const baseUrl = `${getCloudFunctionUrl(endpoint)}/${endpoint}`;
  return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
};

/**
 * Wakes the three PDF functions so the first real export does not pay a cold start.
 * Failures are ignored: this is a best-effort ping.
 */
export const startWarmReportPdfFunctions = () => async () => {
  await Promise.all(
    REPORT_PDF_FUNCTION_NAMES.map((endpoint) =>
      axios({
        method: "post",
        url: buildReportPdfUrl(endpoint),
        data: { coldBoot: true },
      }).catch(() => null)
    )
  );
};

/**
 * Stores the report snapshot server-side and returns the generated PDF url.
 */
export const startReportPdfExport =
  ({ reportType, snapshot, fileName, footerText }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      if (!token) {
        return { ok: false, message: "Missing auth token" };
      }

      const { data: result } = await axios({
        method: "post",
        url: buildReportPdfUrl("startReportPdfExport"),
        data: { reportType, snapshot, fileName, footerText },
        headers: { Authorization: `Bearer ${token}` },
      });

      return { ok: Boolean(result?.ok), url: result?.data?.url };
    } catch (error) {
      const errorFormatted = dispatch(
        LaunchError(error, true, [], { functionName: "startReportPdfExport" })
      );
      return {
        ok: false,
        code: errorFormatted?.code,
        message: errorFormatted?.message || error?.message || "",
      };
    }
  };

/**
 * Public, token-gated. Used by the headless browser rendering the report page.
 */
export const startGetReportPdfSnapshot = (token) => async () => {
  try {
    const { data: result } = await axios({
      method: "get",
      url: buildReportPdfUrl("getReportPdfSnapshot", token),
    });

    return {
      ok: Boolean(result?.ok),
      reportType: result?.data?.reportType,
      snapshot: result?.data?.snapshot,
    };
  } catch (error) {
    store.dispatch(
      LaunchError(error, false, [], {
        functionName: "startGetReportPdfSnapshot",
        capture: "exception",
      })
    );
    return {
      ok: false,
      code: error?.response?.data?.code,
      message: error?.response?.data?.message || error?.message || "",
    };
  }
};
